// CartPage.jsx
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import PatientInfoStep from './Steps/PatientInfoStep';
import ServiceInfoStep from './Steps/ServiceInfoStep';
import PaymentStep from './Steps/PaymentStep';
import Stepper from '@/v2/components/Stepper';
import SuccessStep from './Steps/SuccessStep';
import ErrorStep from './Steps/ErrorStep';

import { useCartData } from './context/CartDataContext';
import { useRegistration } from './context/RegistrationContext';
import useFetch from 'use-http';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { useUserState } from '@/components/context/UserContext';

import {
  buildUiPayload,
  toRegistrationInput,
} from './utils/registrationMapper';
import RegistrationErrorDialog from './Components/RegistrationErrorDialog';
import {
  useCartDispatch,
  useCartState,
} from '@/components/context/CartContext';
import { set } from 'lodash';

const CartPage = () => {
  const [stepIndex, setStepIndex] = useState(0);
  const dispatch = useCartDispatch();
  const [leftToPaysera, setLeftToPaysera] = useState(true);

  const { status } = useParams(); // route param e.g. /cart/:status
  const isSuccessRoute = status === 'success';
  const isErrorRoute = status === 'error';

  // One fixed error message for everything
  const GENERIC_ERROR_MSG =
    'Atsiprašome, tačiau jūsų registracija nepavyko dėl nenumatytos klaidos. Bandykite dar kartą.';

  // Error dialog state (for POST errors in natural flow)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] =
    useState(GENERIC_ERROR_MSG);

  // API clients
  const { post: postOtherServices } = useFetch('/Services/OtherServices');
  const { get: getQuestions } = useFetch('/Services');

  // POST /registrations
  const {
    post: postRegistration,
    response: registrationResponse,
    loading: isFinalizing,
    error: registrationError,
  } = useFetch('/registrations');

  const {
    service,
    services,
    loading,
    setOtherServices,
    setQuestions,
    setQuestionsLoading,
    setQuestionsError,
    selectedTimeSlot,
    timeslotReservedUntil,
    setServices,
  } = useCartData();

  const { userInfo, consultationInfo, paymentInfo } = useRegistration();
  const userState = useUserState();
  const cartState = useCartState();
  const history = useHistory();

  const nextStep = () => setStepIndex((i) => Math.min(i + 1, steps.length));
  const prevStep = () => {
    setStepIndex((i) => {
      if (i === 0) {
        history.goBack();
        return 0;
      }
      return i - 1;
    });
  };

  const stepTitles = useMemo(
    () => ['Registracija', 'Paslauga', 'Apmokėjimas'],
    [],
  );

  // Handle status routes -> jump directly to success/error views
  useEffect(() => {
    if (isSuccessRoute) {
      dispatch({ type: 'RESET_REGISTRATION_ID' });

      // Logged-in users go straight to visits page
      if (userState.isLoggedIn) {
        history.push('/userAccount/visits', { fromPayment: true });
        return;
      }

      // Otherwise show internal success state
      setStepIndex(stepTitles.length);
    } else if (isErrorRoute) {
      setStepIndex(-1); // error route
    }
  }, [
    isSuccessRoute,
    isErrorRoute,
    stepTitles.length,
    userState.isLoggedIn,
    history,
    dispatch,
  ]);

  // Guard: if no service loaded AND not on explicit success/error route, go home

  // Load other services
  useEffect(() => {
    if (!service || loading) return;
    const svc = Array.isArray(service) ? service[0] : service;
    if (!svc?.specialistId) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await postOtherServices('', {
          serviceId: service.serviceId,
          workplaceId: service.workplaceId,
        });
        if (!cancelled) setOtherServices(res);
      } catch (e) {
        console.error('Failed to load other services', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    service,
    loading,
    postOtherServices,
    setOtherServices,
    service?.serviceId,
    service?.workplaceId,
  ]);

  // Load questions
  useEffect(() => {
    if (!service || loading) return;

    let cancelled = false;
    (async () => {
      try {
        setQuestionsLoading(true);
        setQuestionsError(null);
        const res = await getQuestions(
          `/${service.workplaceId}/${service.serviceId}/questions`,
        );
        if (!cancelled) setQuestions(res);
      } catch (e) {
        if (!cancelled)
          setQuestionsError(e?.message || 'Nepavyko gauti klausimų');
        console.error('Failed to load questions', e);
      } finally {
        if (!cancelled) setQuestionsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    service?.serviceId,
    service?.workplaceId,
    loading,
    getQuestions,
    setQuestions,
    setQuestionsError,
    setQuestionsLoading,
  ]);

  useEffect(() => {
    if (service == null && leftToPaysera && !isSuccessRoute && !isErrorRoute) {
      if (cartState.registrationId) {
        history.push('/payment/status/cancel');
      } else {
        history.push('/');
      }
    }
  }, [
    service,
    isSuccessRoute,
    isErrorRoute,
    history,
    cartState?.registrationId,
  ]);

  // Finalize checkout -> build payload -> POST /registrations
  const handleFinalizeCheckout = useCallback(
    async (paymentFormValues) => {
      const mergedPayment = paymentInfo
        ? { ...paymentInfo, ...paymentFormValues }
        : paymentFormValues;

      if (services && services.length > 1) {
        services.forEach((svc) => {
          svc.isPaidLaboratoryService = selectedTimeSlot.isPaid;
        });
      }
      const uiPayload = buildUiPayload({
        svc: services,
        userInfo,
        consultationInfo,
        paymentMerged: mergedPayment,
        selectedTimeSlot,
        timeslotReservedUntil,
        userState,
        childSelected: !!userInfo?.childSelected,
        selectedChildEspbiEsi: userInfo?.selectedChildEspbiEsi || '',
      });


      const registrationInput = toRegistrationInput(uiPayload);
      try {
        const result = await postRegistration('', registrationInput);
        if (!registrationResponse.ok) {
          // 1) Natural flow error -> show modal, stay on current step
          console.error('❌ Backend rejected registration:', result);
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          return;
        }

        // 2) Paysera URL? -> redirect
        const payseraUrl = result?.payseraUrl || result?.PayseraUrl;
        if (typeof payseraUrl === 'string' && payseraUrl.trim() !== '') {
          dispatch({
            type: 'SET_REGISTRATION_ID',
            registrationId: result.registrationId,
            orderType: 'registrationService',
          });
          window.location.href = payseraUrl;
          setLeftToPaysera(false);
          setServices(null);
          return;
        }
        window.location.replace('/cart/success');
        // 1) Natural success (no Paysera URL) -> show SuccessStep
        setStepIndex(stepTitles.length);
        setTimeout(() => setServices(null), 100);
      } catch (err) {
        console.error('❌ Registration error:', err);
        // Natural flow network/exception error -> modal
        setErrorDialogMessage(GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
      }
    },
    [
      service,
      paymentInfo,
      userInfo,
      consultationInfo,
      selectedTimeSlot,
      timeslotReservedUntil,
      postRegistration,
      registrationResponse,
      userState,
      stepTitles.length,
    ],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stepIndex]);

  const steps = useMemo(
    () => [
      {
        title: stepTitles[0],
        content: <PatientInfoStep />,
      },
      { title: stepTitles[1], content: <ServiceInfoStep /> },
      {
        title: stepTitles[2],
        content: (
          <PaymentStep
            onFinalize={handleFinalizeCheckout}
            isSubmitting={isFinalizing}
            submitError={registrationError?.message}
          />
        ),
      },
    ],
    [stepTitles, handleFinalizeCheckout, isFinalizing, registrationError],
  );

  useEffect(() => {
    const unblock = history.block(() => {
      const nextPath = window.location.pathname;

      if (
        nextPath.startsWith('/services') &&
        stepIndex > 0 &&
        !isSuccessRoute
      ) {
        setStepIndex((prev) => prev - 1);
        return false;
      }
      return true;
    });

    return () => unblock();
  }, [history, stepIndex]);

  const isCompleted = stepIndex === steps.length; // natural success
  const isError = stepIndex === -1; // explicit error route

  const currentStepEl =
    !isCompleted && !isError && steps[stepIndex]?.content
      ? React.cloneElement(steps[stepIndex].content, {
          stepIndex,
          stepNumber: stepIndex + 1,
          totalSteps: steps.length,
          isActive: true,
          isCompleted,
          goNext: nextStep,
          goPrev: prevStep,
          setStepIndex,
        })
      : null;

  // Loading guard (only when not on explicit success/error routes)
  if (loading && !service && !isSuccessRoute && !isErrorRoute) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium">Kraunama...</div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1440px] mx-auto h-full p-4 font-primary">
      <div className="mb-4">
        <Stepper
          steps={stepTitles}
          currentStep={
            isCompleted || isSuccessRoute || isErrorRoute
              ? steps.length + 1 // 4 -> all green
              : stepIndex + 1
          }
        />
      </div>

      <div className="pb-4 min-h-40">
        {isCompleted || isSuccessRoute ? (
          <SuccessStep />
        ) : isErrorRoute ? (
          <ErrorStep />
        ) : (
          currentStepEl
        )}
      </div>

      <RegistrationErrorDialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        message={errorDialogMessage}
      />
    </div>
  );
};

export default CartPage;
